import { type BoardState, type Edge, groupMembers, type Item } from '@shared/board'
import { centre, containsPoint, overlaps, pad, type Rect, union } from '@shared/whiteboard/geometry'
import { type Mention, parseMention } from './mentions'

// Embeds (DESIGN.md "UI"): the AI shows part of the board inside a message
// with markdown image syntax, `![title](item://ID)` or `![](region://…)`. A
// single thing shows as itself; a group, a connection or a region shows as a
// small read-only scene, edges included. Embeds show the board as it is now;
// one whose things changed after its turn ended says so.

/** Room around a scene's things, in board pixels. */
export const SCENE_PAD = 32

export type Scene = {
  items: Item[]
  edges: Edge[]
  /** The part of the board shown, in board coordinates. */
  frame: Rect
  /** Ids named by the embed that are no longer on the board. */
  missing: string[]
  /**
   * For an area: things that overlap it without their centre being in it.
   * Not drawn as members, but a change to them may change what the area shows.
   */
  nearby: string[]
}

export type EmbedTarget =
  | { kind: 'item'; item: Item }
  | { kind: 'scene'; scene: Scene }
  /** Everything it named is gone. */
  | { kind: 'gone' }

/** The pointer an image source names, if it is one of ours. */
export function parseEmbed(src: string | undefined): Mention | null {
  return src ? parseMention(src) : null
}

/** Groups bring their members: a group without its contents says little. */
function withMembers(s: BoardState, items: Item[]): Item[] {
  const out = new Map<string, Item>()
  for (const i of items) {
    out.set(i.id, i)
    if (i.kind === 'group') for (const m of groupMembers(s, i.id)) out.set(m.id, m)
  }
  return [...out.values()]
}

function scene(
  s: BoardState,
  items: Item[],
  missing: string[],
  frame?: Rect,
  nearby: string[] = [],
): Scene {
  const ids = new Set(items.map((i) => i.id))
  const edges = [...s.edges.values()].filter((e) => ids.has(e.from) && ids.has(e.to))
  const box = frame ?? pad(union(items) ?? { x: 0, y: 0, w: 0, h: 0 }, SCENE_PAD)
  return { items, edges, frame: box, missing, nearby }
}

export function resolveEmbed(m: Mention, s: BoardState): EmbedTarget {
  switch (m.kind) {
    case 'item': {
      const item = s.items.get(m.id)
      if (!item) return { kind: 'gone' }
      if (item.kind !== 'group') return { kind: 'item', item }
      return { kind: 'scene', scene: scene(s, withMembers(s, [item]), []) }
    }
    case 'edge': {
      // Deleting an end deletes the connection too, so a live edge has both;
      // an end can still be missing while the board catches up.
      const e = s.edges.get(m.id)
      if (!e) return { kind: 'gone' }
      const ends = [s.items.get(e.from), s.items.get(e.to)].filter((i) => !!i)
      if (!ends.length) return { kind: 'gone' }
      const missing = [e.from, e.to].filter((id) => !s.items.has(id))
      return { kind: 'scene', scene: scene(s, ends, missing) }
    }
    case 'items': {
      const found = m.ids.map((id) => s.items.get(id)).filter((i) => !!i)
      if (!found.length) return { kind: 'gone' }
      const missing = m.ids.filter((id) => !s.items.has(id))
      return { kind: 'scene', scene: scene(s, withMembers(s, found), missing) }
    }
    case 'rect': {
      // A thing is in the area when its centre is, as for group membership.
      const inside: Item[] = []
      const nearby: string[] = []
      for (const i of s.items.values()) {
        if (i.kind === 'group') continue
        if (containsPoint(m.rect, centre(i))) inside.push(i)
        else if (overlaps(m.rect, i)) nearby.push(i.id)
      }
      return { kind: 'scene', scene: scene(s, inside, [], m.rect, nearby) }
    }
  }
}

/** At most this many cards read better stacked than drawn (see `stackedCards`). */
export const STACK_MAX = 3

/**
 * A scene of a few cards and nothing else (a group's frame aside) is there to
 * be read: drawn to scale it can shrink to unreadable text (two cards one
 * above the other in a narrow message). Such a scene shows as the cards
 * themselves, reflowed, top to bottom then left to right, with the
 * connections between them listed. Anything larger, or with other things in
 * it, stays a drawing: there, where things are is part of what it shows.
 */
export function stackedCards(sc: Scene): { cards: Item[]; links: Edge[] } | null {
  const things = sc.items.filter((i) => i.kind !== 'group')
  if (!things.length || things.length > STACK_MAX || things.some((i) => i.kind !== 'card'))
    return null
  return { cards: [...things].sort((a, b) => a.y - b.y || a.x - b.x), links: sc.edges }
}

/**
 * Whether what an embed shows changed after `since` (the end of the turn that
 * said it: anything the AI did in its own turn is what the message is about).
 * `touched` holds the server's last-change times ("item:ID", "edge:ID",
 * "text:ID"). A lone card changes with its text; anything else with any
 * change, position included, since a scene is about where things are. For
 * an area, things overlapping it count too (one may have moved half in).
 * Something that moved right out of an area is not seen: that needs history.
 */
export function changedSince(
  target: EmbedTarget,
  since: number,
  touched: Readonly<Record<string, number>>,
): boolean {
  const after = (ref: string) => (touched[ref] ?? 0) > since
  switch (target.kind) {
    case 'gone':
      return false
    case 'item': {
      const { item } = target
      return item.kind === 'card' ? after(`text:${item.id}`) : after(`item:${item.id}`)
    }
    case 'scene': {
      const { items, edges, missing, nearby } = target.scene
      return (
        missing.length > 0 ||
        items.some((i) => after(`item:${i.id}`) || after(`text:${i.id}`)) ||
        edges.some((e) => after(`edge:${e.id}`)) ||
        nearby.some((id) => after(`item:${id}`))
      )
    }
  }
}

/**
 * What a scene draws, as a string: equal keys draw the same picture (card
 * text is live inside the drawing, so it is not part of the key). Lets an
 * embed keep its scene, and skip redrawing, through unrelated board changes.
 */
export function sceneKey(sc: Scene): string {
  const f = sc.frame
  return [
    `${f.x},${f.y},${f.w},${f.h}`,
    sc.items.map((i) => `${i.id}:${i.version}:${i.x},${i.y},${i.w},${i.h}`).join(';'),
    sc.edges.map((e) => `${e.id}:${e.version}`).join(';'),
    sc.missing.join(','),
    sc.nearby.join(','),
  ].join('|')
}
